import * as R from 'ramda';
import { put, call, delay } from 'redux-saga/effects';
// constants
import * as C from '../../constants';
// utilities
import { ENDPOINTS } from './endpoints';
import { windowLocationReload } from '../window';
import { getAuthTokenFromStorage, removeAuthTokenFromStorage } from '../storage';
import {
  ifElse,
  getTranslation,
  getPropFromObject,
  isNotNilAndNotEmpty,
} from '../common';
// feature auth
import { loginRoutine } from '../../features/auth/routines';
////////////////////////////////////////////////

const PROTOCOL = 'https';
const API_DOMAIN = 'api-market-list.amoustms.app';

const makeRequestHeaders = optionalHeaders => {
  const token = getAuthTokenFromStorage();

  const sessionHeaders = new Headers();

  if (optionalHeaders) {
    R.mapObjIndexed((value, field) => {
      if (R.and(
        R.equals(field, C.CONTENT_TYPE),
        R.equals(value, C.MULTIPART_FORM_DATA),
      )) return;

      sessionHeaders.append(field, value);
    }, optionalHeaders);
  } else {
    sessionHeaders.append('Content-Type', 'application/json');
  }

  if (token) {
    sessionHeaders.append('Authorization', `Bearer ${token}`);
  }

  return sessionHeaders;
};

const waitForResponse = async res => {
  const responseText = await res.text();
  const isNotEmpty = R.compose(R.not, R.isEmpty, R.trim);
  const status = getPropFromObject(C.FIELD_STATUS, res);

  const parseJSON = R.when(
    isNotEmpty,
    R.compose(
      JSON.parse,
      R.trim,
    ),
  );

  const response = parseJSON(responseText);
  const error = R.pathOr(null, ['error'], response);

  if (R.equals(status, 401)) {
    return {
      status,
      data: response,
      error: getTranslation('errors.sessionHasExpired', 'Your session has expired'),
    };
  }

  return { error, status, data: response };
};

const sendRequest = async (method, path, options) => {
  const protocol = R.or(options?.protocol, `${PROTOCOL}://`);
  const apiDomain = R.or(options?.apiDomain, API_DOMAIN);
  const searchParams = ifElse(
    options?.queryParams,
    `?${new URLSearchParams(options?.queryParams).toString()}`,
    '',
  );
  const url = `${protocol}${apiDomain}/${path}${searchParams}`;
  const headers = makeRequestHeaders(options?.headers);

  let body = options?.body;

  let redirect = 'manual';

  if (options?.redirect) {
    redirect = options.redirect;
  }

  if (R.not(body)) {
    try {
      const data = options?.data;

      body = JSON.stringify(data);
    } catch (error) {
      console.info('Can\'t stringify data fot the body');
    }
  }

  const requestOptions = {
    headers,
    redirect,
    method: R.toUpper(method),
    ...(R.and(body, { body })),
  };

  const res = await fetch(url, requestOptions);

  return waitForResponse(res);
};

const isResponseSuccess = (status, data, msg, partialSuccessMsg) => {
  if (R.and(R.gte(status, 200), R.lt(status, 300))) {
    switch (status) {
      case 200: {
        if (isNotNilAndNotEmpty(msg)) {
          console.info(`====================== success ${msg} success ======================`);
        }

        break;
      }
      case 202: {
        if (isNotNilAndNotEmpty(data.errors)) {
          console.error(
            `============================== ${R.or(msg, 'partial success')} ==============================`,
            `${R.values(data.errors).join(', ')}: ${partialSuccessMsg}`,
          );
        } else {
          console.info(`====================== success ${msg} success ======================`);
        }

        break;
      }
      default: {
        break;
      }
    }

    return true;
  }

  return false;
};

const logError = (msg, error) => {
  console.error(`============================== ${msg} ==============================`, error);
};

const handleException = (error, msg) => logError(R.or(msg, 'exception'), error);

function* handleFailResponse(res) {
  const { data, status } = res;

  if (R.equals(status, 401)) {
    yield put(loginRoutine.failure());

    yield call(removeAuthTokenFromStorage);

    yield delay(2000);

    yield call(windowLocationReload);
  }

  logError(status, data);
}

const stringifyAndParseJson = value => JSON.parse(JSON.stringify(value));

const transformUsedReportValues = R.over(
  R.lensProp('searchCriteria'),
  R.compose(
    R.map(
      R.compose(
        R.reject(R.isNil),
        R.reject(R.isEmpty),
        R.omit(['groupId', 'dateRelative', 'withoutOrGroup']),
      ),
    ),
    R.defaultTo([]),
  ),
);

const downloadFileRequest = path => `${PROTOCOL}://${API_DOMAIN}/${ENDPOINTS.fetchBoard}/report/${path}`;

export {
  logError,
  sendRequest,
  handleException,
  isResponseSuccess,
  handleFailResponse,
  makeRequestHeaders,
  downloadFileRequest,
  stringifyAndParseJson,
  transformUsedReportValues,
};
